import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import moment from "moment";
import ButtonElement from "../../Common/ButtonElement";
import TableComponent from "../components/TableComponent";
import SearchBox from "../components/SearchComponent";
import DateRangeSelector from "../../Common/DateRangeElement";
import Billing from "./components/Billing";
import RequestModal from "../CompanyPage/components/RequestModal";
import AddCompany from "../CompanyPage/components/AddCompany";
import { DELETE_POP_UP, downloadCsv, formatDate, isValidObject, reverseDateFormat } from "../../utils";
import { getVehicles } from "../../actions/vehicles";
import DownloadDocxButton from "./components/GstCertificate";
import { appApis, headerColumn, isInovize } from "../../constants";
import { useApiRequest } from "../../hooks/useApiRequest";
import ToggleSwitch from "../../Common/Checkbox";
import { MdBlock, MdOutlineFileDownload } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import VehicleStatus from "./components/VehicleStatus";
import SearchableDropdown from "../../Common/SearchDropdown";
import { TiPencil } from "react-icons/ti";
import { GrStatusGood } from "react-icons/gr";
import { MdAddCard } from "react-icons/md";
import { FaFileInvoiceDollar } from "react-icons/fa6";

const VehiclePage = () => {
    const [state, setState] = useState({
        vehicles: [],
        status: null,
        addVehicle: false,
        addCompany: false,
        showBilling: false,
        billingData: null,
        loading: false,
        statusChange: false,
        vehicleActions: [],
        search: "",
        status: "",
        company: "",
        companyId: "",
        current: 1,
        total: 1,
        firstCall: false,
        vehicleData: {}
    });
    const { request, loading: actionStatus } = useApiRequest();
    const { request: downloadRequest, loading: isdownloading } = useApiRequest();
    const { request: deleteRequest, loading: isDeleteing } = useApiRequest();

    const { companies } = useSelector(({ companies }) => ({
        companies: companies?.companies ?? []
    }), shallowEqual);

    const { addVehicle, showBilling, addCompany, vehicles, firstCall, loading, current, total, search, billingData, vehicleData, statusChange, vehicleActions, status, company, companyId } = state;

    const getVehicleActions = () => {
        request({
            url: appApis.getVehicleActions,
            showToast: false,
            onSuccess: (data) => {
                setState(prev => ({...prev, vehicleActions: data?.data || []}));
            }
        })
    }

    const changeVehicleStatus = (data, actionId, remark = "") => {
        request({
            url: `${appApis.vehicleStatus}?vehicleId=${data?.vehicleId}`,
            method: "PUT",
            body: { 
                isActive: data?.isActive ? false : true,
                actionId,
                remark 
            },
            onSuccess: () => {
                paginationAction(1);
                setTimeout(() => setState(prev => ({ ...prev, statusChange: false, vehicleData: {} })), 1000);
            }
        })
    }

    const getVehicleData = (date, pageIndex = null) => {
        let query = `?page=${pageIndex ? pageIndex : current}&limit=200`;
        if (search) query = `${query}&keyword=${search}`;
        if (status) query = `${query}&isActive=${status == "Enable" ? 1 : 0}`;
        if (company) query = `${query}&companyId=${companyId}`;
        if(date?.startDate && date?.endDate) query =`${query}&start_date=${reverseDateFormat(date?.startDate ?? "")}&end_date=${reverseDateFormat(date?.endDate ?? "")}`;

        setState(prev => ({ ...prev, loading: true, firstCall: true }));
        getVehicles(query).then(response => {
            const { data } = response.data;
            if (isValidObject(data)) {
                setState(prev => ({ ...prev, vehicles: data?.vehicles ?? [], total: data?.total ?? 1 }));
            }
        }).catch(ex => { }).finally(() => setState(prev => ({ ...prev, loading: false })))
    }

    const paginationAction = (pageIndex) => {
        setState(prev => ({ ...prev, current: pageIndex }));
        getVehicleData(null, pageIndex)
    }

    const downloadData = () => {
        downloadRequest({
            url: `${appApis.downloadVehiclesData}`,
            showToast: false,
            onSuccess: (responseData) => {
                downloadCsv(responseData ?? "", "vehicles");
            }
        })
    }

    const removeRequest = (id) => {
        if (id) {
            DELETE_POP_UP(() => deleteRequest({
                method: "DELETE",
                url: `api/v1/vehicles/${id}`,
                onSuccess: () => paginationAction(1)
            }))
        }
    }

    const vehicleStatusAction = (data) => {
        if(data?.isActive) setState(prev => ({ ...prev, statusChange: true, vehicleData: data }));
        else {
            const enableAction = vehicleActions?.find(veh => veh.action_code.includes("ENABLE"));
            if(enableAction) DELETE_POP_UP(() =>changeVehicleStatus(data, enableAction?.action_id, ""));
        }
    }

    useEffect(() => {
        if(firstCall) paginationAction(1);
    }, [search, status, company])

    useEffect(() => {
        const startdateupdate = moment().subtract(29, 'days').format("DD-MM-YYYY");
        const enddateupdate = moment().format("DD-MM-YYYY");
        getVehicleData({ startDate: startdateupdate, endDate: enddateupdate });
        getVehicleActions();
    }, [])

    return <div className="vehicle-container">
        <SearchBox placeholder="Search here..." className="mb-3" value={search} onChange={(e) => setState(prev => ({...prev, search: e.target.value }))} />
        <TableComponent col={4} breakPoint="lg" title={"Vehicles Management"} headers={headerColumn.vehicles} data={vehicles} current={current} total={total} tableLoading={loading} paginationAction={paginationAction} render={(tdData, index) => <tr key={index}>
            <td>{(index ?? 0) + 1}</td>
            <td>{tdData?.vehicleRegno ?? "-"}</td>
            <td>{tdData?.companyName ?? "-"}</td>
            <td>{tdData?.gpsImei ?? "-"}</td>
            <td>{tdData?.device_model ?? "-"}</td>
            <td>{tdData?.PrimarySim?.iccid ?? "-"}</td>
            <td>{tdData?.PrimarySim?.sim_number ?? "-"}</td>
            <td>{tdData?.SecondarySim?.sim_number ?? "-"}</td>
            <td>{tdData?.installationDate ?? "-"}</td>
            <td>{tdData?.vehicleExpiryDate ?? "-"}</td>
            <td>{tdData?.software_provider_name ?? "-"}</td>
            <td>{(tdData?.gst_applicable) ? "Yes" : "No"}</td>
            <td>{tdData?.billing_type ?? "-"}</td>
            <td>{tdData?.billing_cycle ?? "-"}</td>
            <td>{tdData?.billing_amount ?? "-"}</td>
            <td>{tdData?.billing_company_name ?? "-"}</td>
            <td>{formatDate(tdData?.last_billing_date ?? "")}</td>
            <td>{tdData?.renewal_amount ?? "-"}</td>
             <td>
                {/* <div className={`d-flex justify-content-center align-items-center px-3`}>
                    <ToggleSwitch
                        value={tdData?.isActive}
                        onChange={() => changeVehicleStatus(tdData?.vehicleId, tdData?.isActive ? false : true)}
                    />
                </div> */}
                {tdData?.isActive ? <span className="badge bg-success">Enabled</span> : <span className="badge bg-secondary">Disabled</span>}
            </td>
            <td>
                <ButtonElement text={<TiPencil size={20} />} cssClass="more-radius mx-1 px-4 small-btn" onClick={() => setState(prev => ({ ...prev, addVehicle: true, vehicleData: tdData }))} variant="btn-outline-edit" loading={false} />

                <DownloadDocxButton vehicleNo={tdData?.vehicleRegno ?? ""} imei={tdData?.gpsImei ?? ""} simNo={tdData?.PrimarySim?.sim_number} installDate={tdData?.installationDate} />

                <ButtonElement text={tdData?.billing_company_name ? <FaFileInvoiceDollar size={20} /> : <MdAddCard size={20} />} cssClass="more-radius mx-1 small-btn" onClick={() => setState(prev => ({ ...prev, showBilling: true, billingData: tdData }))} variant={`btn-outline-${tdData?.billing_company_name ? "success" : "preview"}`} loading={false} />

                <ButtonElement text={<RiDeleteBin5Line size={20} />} cssClass="more-radius px-4" onClick={() => removeRequest(tdData?.vehicleId ?? 0)} variant="btn-delete-icon small-btn" loading={isDeleteing} />

                <ButtonElement text={tdData?.isActive ? <MdBlock size={20} /> : <GrStatusGood size={20} />} cssClass="more-radius px-4" onClick={() => vehicleStatusAction(tdData)} variant={`btn-outline-${tdData?.isActive ? "red" : "success"} small-btn mx-1`} />
            </td>
        </tr>} FilterComponent={<>
            <div className="me-2">
                <SearchableDropdown
                    placeholder={"Vehicle Status"}
                    value={status}
                    error={false}
                    options={[{ name: "Enable", id: 1 }, { name: "Disable", id: 0 }]}
                    onSelect={(value) => setState(prev => ({ ...prev, status: value?.name }))}
                />
            </div>
            <div className="me-2">
                <SearchableDropdown
                    placeholder={"Company Name"}
                    value={company}
                    error={false}
                    options={companies || []}
                    onSelect={(value) => setState(prev => ({ ...prev, company: value?.name, companyId: value?.id }))}
                />
            </div>
            <DateRangeSelector updateRange={getVehicleData} />
            <ButtonElement loading={isdownloading} text={<MdOutlineFileDownload fontSize={24} />} cssClass="small-radius me-1 mb-1" onClick={downloadData} variant="btn-outline-grey" />
            <ButtonElement text={"Add Vehicle"} cssClass="small-radius me-1 mb-1" onClick={() => setState(prev => ({ ...prev, addVehicle: true }))} variant="btn-black" loading={false} />
        </>} />

        {addVehicle ? <RequestModal show={addVehicle} showCompany={() => setState(prev => ({ ...prev, addVehicle: false, addCompany: true }))} hideModal={() => setState(prev => ({ ...prev, addVehicle: false, vehicleData: {} }))} defaultData={vehicleData} sucessAction={getVehicleData} isVehicleAdd={true} /> : null}
        {showBilling ? <Billing defaultData={billingData} show={showBilling} getInvoiceData={getVehicleData} hideModal={() => setState(prev => ({ ...prev, showBilling: false, billingData: null }))} /> : null}
        {addCompany ? <AddCompany show={addCompany} hideModal={() => setState(prev => ({ ...prev, addCompany: false }))}/> : null}

        {statusChange ? <VehicleStatus show={statusChange} vehicleActions={vehicleActions} hideModal={() => setState(prev => ({ ...prev, statusChange: false, vehicleData: {} }))} vehicleData={vehicleData} statusAction={(actionId, remark) => changeVehicleStatus(vehicleData, actionId, remark)} loading={actionStatus} /> : null}
    </div>
}

export default VehiclePage;